import { useDynamicContext } from '@dynamic-labs/sdk-react-core';

import { isSolanaChainId, SOLANA_NETWORKS, SolanaChainId } from '@/config/solana';
import { isStellarChainId, STELLAR_NETWORKS } from '@/config/stellar';
import { supportedChainIds } from '@/utils/networksConfig';

interface UseGetChainInfoReturnType {
  getChainName: (chainId: string | number | null | undefined) => string;
  getChainIconUrl: (chainId: string | number | null | undefined) => string;
  getNetworkChainId: (networkName: string) => number;
}

export default function useGetChainInfo(): UseGetChainInfoReturnType {
  const { networkConfigurations } = useDynamicContext();
  const evmNetworks = networkConfigurations?.['evm'];

  return {
    getChainName: (chainId: string | number | null | undefined) => {
      if (chainId === null || chainId === undefined) return '';

      const numericChainId = typeof chainId === 'string' ? Number(chainId) : chainId;

      // Check Solana/SVM networks first
      if (isSolanaChainId(numericChainId)) {
        return SOLANA_NETWORKS[numericChainId as SolanaChainId]?.name || '';
      }

      if (isStellarChainId(numericChainId)) {
        return STELLAR_NETWORKS[numericChainId]?.name || '';
      }

      // Check EVM networks from Dynamic
      const dynamicNetwork = evmNetworks?.find((n) => n.chainId === numericChainId);
      if (dynamicNetwork?.vanityName || dynamicNetwork?.name) {
        return dynamicNetwork.vanityName || dynamicNetwork.name || '';
      }

      // Fallback to local config
      const localNetwork = supportedChainIds.find((n) => n.chainId === numericChainId);
      return localNetwork?.name || '';
    },

    getChainIconUrl: (chainId: string | number | null | undefined) => {
      if (chainId === null || chainId === undefined) return '';
      const numericChainId = typeof chainId === 'string' ? Number(chainId) : chainId;

      // Check Solana/SVM networks first
      if (isSolanaChainId(numericChainId)) {
        const solConfig = SOLANA_NETWORKS[numericChainId as SolanaChainId];
        if (solConfig) {
          return numericChainId === 102 ? '/assets/webp/fogo.webp' : 'https://app.dynamic.xyz/assets/networks/sol.svg';
        }
      }

      if (isStellarChainId(numericChainId)) {
        return '/assets/webp/stellar.webp';
      }

      return evmNetworks?.find((n) => n.chainId === numericChainId)?.iconUrls?.[0] ?? '';
    },

    getNetworkChainId: (networkName: string) =>
      (evmNetworks?.find((n) => n.key === networkName)?.chainId as number) ?? 0,
  };
}
